import React, { useContext, useEffect, useState } from 'react'
import { LOGGER } from '@cicero/data/utils'
import { Transforms } from 'slate'
import { ReactEditor, useEditor } from 'slate-react'
import { of } from 'rxjs'
import { FormControl, Icon, IconButton, TextField, Timestamp, makeStyles, useObservable } from '@cicero/ui'
import { EditorContext, IDashboardWidgetElement, IEditorWidget } from '../EditorService'
import { CommentSchema, CommentThreadSchema, StoreContext, useModel } from '../../models'
import { UserTile } from 'user'

export const CommentThreadWidget = ({ leaf }: { leaf: ICommentThreadWidgetConfig }) => {
  const { commentWrapper, commentList } = useStyles()
  const { thread_id } = leaf
  const CommentThread = useModel(CommentThreadSchema)
  const Comment = useModel(CommentSchema)
  const editor = useEditor()
  const service = useContext(EditorContext)
  const store = useContext(StoreContext)

  const [value, setValue] = useState('')
  const thread = useObservable(() => CommentThread.GET$(thread_id), null, [thread_id])

  const onKeyPress = (e: any) => {
    switch (e.key) {
      case 'Enter':
        if (e.shiftKey !== true) {
          e.preventDefault()
          submitComment()
        }
    }
  }

  const submitComment = async () => {
    const { session } = store
    if (!session) { return }
    setValue('')
    let t = thread

    if (!t) {
      const page_id = service.page.id
      t = await CommentThread.CREATE({ page_id })
      CommentThreadCommands.setThreadId(editor, t, leaf)
    }

    const comment = await Comment.CREATE({ content: value, thread_id: t.id, user_id: session.user.id })
    LOGGER.debug('created comment', t, comment)
  }

  useEffect(() => {
    // If we go from temp id to uuid, save update the widget
    if (leaf.thread_id && thread?.meta?.uuid) {
      CommentThreadCommands.setThreadId(editor, thread, leaf)
    }
  }, [thread?.meta?.uuid])

  // LOGGER.debug(Comment, CommentThread, leaf)
  // TODO Submit on enter unless shift is pressed - if e.keycode + e.shiftPressed ? submit : newline
  return <span className={commentWrapper}>
    { thread ? <CommentList thread={thread} /> : <div className={commentList}></div> }
    <FormControl fullWidth onSubmit={async () => submitComment()}>
      <TextField
        variant="outlined"
        placeholder="Add a Comment"
        multiline
        rows={3}
        value={value}
        disabled={!store.session?.user}
        onKeyUp={(e) => onKeyPress(e)}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          endAdornment: <IconButton onClick={async () => submitComment()}><Icon>play_arrow</Icon></IconButton>,
        }} />
    </FormControl>
  </span>
}

export const CommentList = ({ thread }: { thread: CommentThreadSchema }) => {
  const { commentList } = useStyles()
  const Comment = useModel(CommentSchema)
  const comments = useObservable<CommentSchema[]>(() => thread ? Comment.QUERY$(CommentSchema.QUERIES.orderedThread(thread.id)) : of([]), [], [thread])

  return <ul className={commentList}>
    { comments.map(comment => <li key={comment.id}>
      <header>
        <UserTile id={comment.user_id} size={34} />
        <sub><Timestamp date={comment.created_at} /></sub>
      </header>
      <p>{comment.content}</p>
    </li>) }
  </ul>
}

export const CommentThreadCommands = {
  setThreadId(editor: ReactEditor, thread: CommentThreadSchema, leaf: ICommentThreadWidgetConfig) {
    const path = ReactEditor.findPath(editor, leaf)
    // TODO Page Content with comments needs to clean up these IDs before syncing
    Transforms.setNodes(editor, { thread_id: thread.id }, { at: path })
  },
}

export const commentThreadWidget: IEditorWidget = {
  id: 'commentthread',
  name: 'Comment Thread',
  description: 'A thread for users to discuss something',
  image: require('./img/commentthread.png'),
  renderer: (leaf: ICommentThreadWidgetConfig) => <CommentThreadWidget leaf={leaf} />,
}

export interface ICommentThreadWidgetConfig extends IDashboardWidgetElement {
  thread_id: string
}

const useStyles = makeStyles(({ palette }) => ({
  commentWrapper: {
    height: '100%',
    width: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  commentList: {
    flexGrow: 1,
    listStyleType: 'none',
    padding: 0,
    margin: 0,

    '& li': {
      border: `1px solid ${palette.grey[100]}`,
      borderRadius: 6,
      margin: '6px 0',

      '& header': {
        padding: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: palette.grey[100],
      },

      '& p': {
        padding: 12,
        margin: 0,
      },
    },
  },
}))
